import { useDispatch, useSelector } from 'react-redux'
import { Popup } from '../Popup/Popup'
import {
  closeCurrentPopup,
  setDestinationView,
} from '../../Store/InterfaceSlice'
import { useLocalization } from '../../Hooks/useLocalization'
import { Button } from '../../Components/Button/Button'
import { useEffect, useState } from 'react'
import {
  getSimplifiedVatNumber,
  isValidVatNumber,
} from '../../Utils/Validations'
import { TextInput } from '../../Components/TextInput/TextInput'
import { type RootState } from '../../Store/Store'
import {
  setCompanies,
  setIsCertificatesRequested,
  setIsCompaniesRequested,
  type Company,
  type Certificate,
  type CertificateCompanyBranch,
} from '../../Store/DataSlice'
import { getAuthorizationHeader } from '../../Orchestration/Authentication'
import './ChangeCompanyVatPopup.scss'
import { LoadingSpinner } from '../../Components/LoadingSpinner/LoadingSpinner'

interface ChangeCompanyVatPopupProps {
  vatNumber: string
}

export function ChangeCompanyVatPopup({
  vatNumber: oldVatNumber,
}: ChangeCompanyVatPopupProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('company')
  const generalLocalization = useLocalization('general')
  const [renameInProgress, setRenameInProgress] = useState(false)

  // get companies
  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])

  const allCompanies = useSelector((state: RootState) => state.data.companies)
  const company = allCompanies.find((c) => c.vatNumber === oldVatNumber)

  // get certificates
  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])
  const allCertificates = useSelector(
    (state: RootState) => state.data.enrichedCertificates
  )

  const [vatNumberIsValid, setVatNumberIsValid] = useState(false)
  const [newVatNumber, setNewVatNumber] = useState(oldVatNumber)

  const onVatNumberChange = (vatNumber: string) => {
    const simplifiedVatNumber = getSimplifiedVatNumber(vatNumber)
    const isValid =
      simplifiedVatNumber?.length > 2 &&
      isValidVatNumber(simplifiedVatNumber) &&
      !allCompanies.some((c) => c.vatNumber === simplifiedVatNumber)

    setVatNumberIsValid(isValid)
    setNewVatNumber(simplifiedVatNumber)
  }

  const updateVatNumber = () => {
    if (renameInProgress || !vatNumberIsValid || company == null) return

    setRenameInProgress(true)
    const newCompany = { ...company, vatNumber: newVatNumber }

    renameVatNumber(oldVatNumber, newVatNumber, newCompany, allCertificates)
      .then(() => {
        dispatch(setIsCertificatesRequested(false))
        dispatch(
          setCompanies([
            ...allCompanies.filter((c) => c.vatNumber !== oldVatNumber),
            newCompany,
          ])
        )
        dispatch(closeCurrentPopup())
        setTimeout(() => {
          dispatch(
            setDestinationView({
              destinationView: 'company-detail',
              detailKey: newVatNumber,
            })
          )
        }, 5)
      })
      .catch((err) => {
        console.warn(`company vat number change failed: `, err)
      })
  }

  return (
    <Popup
      className="change-company-vat-popup"
      title={localization.text(
        'change-company-vat',
        'Wijzig onderneming ondernemingsnummer'
      )}
    >
      <div className="old-vat-number">
        {localization.text(
          'old-vat-number',
          'Verander ondernemingsnummer {vatNumber}',
          { vatNumber: oldVatNumber }
        )}
      </div>

      <TextInput
        label={localization.text('vat-number', 'Ondernemingsnummer')}
        placeholder="BE0999999999"
        onChange={onVatNumberChange}
        isValid={vatNumberIsValid}
        isReadOnly={renameInProgress}
        value={newVatNumber}
        helperText={
          !vatNumberIsValid
            ? localization.text(
                'invalid-vat-number',
                'Geef een geldig vat nummer startende met de landcode'
              )
            : undefined
        }
      />

      {renameInProgress ? (
        <div className="loading-wrapper">
          <LoadingSpinner color="dark" />
        </div>
      ) : (
        <div className="button-wrapper">
          <Button
            importance={'secondary'}
            text={generalLocalization.text('cancel', 'Annuleer')}
            disabled={renameInProgress}
            onClick={() => dispatch(closeCurrentPopup())}
          />
          <Button
            importance={'primary'}
            text={generalLocalization.text('save', 'Opslaan')}
            disabled={renameInProgress || !vatNumberIsValid}
            onClick={updateVatNumber}
          />
        </div>
      )}
    </Popup>
  )
}

async function renameVatNumber(
  oldVatNumber: string,
  newVatNumber: string,
  newCompany: Company,
  allCertificates: Certificate[]
) {
  // create new company with new vat number and old company data
  const companyAddResult = await fetch(
    `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/company-add`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthorizationHeader(),
      },
      body: JSON.stringify({
        company: newCompany,
      }),
    }
  )
  console.log('Create company for new VAT number response: ', companyAddResult)

  // update all certificates to use new vat number
  const relevantCertificates = allCertificates.filter((c) =>
    c.companyBranches.some((cb) => cb.vatNumber === oldVatNumber)
  )

  const allCertificateUpdatePromises = relevantCertificates.map(
    async (certificate) => {
      const updatedCertificate = {
        ...certificate,
        companyBranches: certificate.companyBranches.map((cb) =>
          cb.vatNumber === oldVatNumber
            ? ({
                ...cb,
                vatNumber: newVatNumber,
              } satisfies CertificateCompanyBranch)
            : cb
        ),
      }
      return await fetch(
        `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-add`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthorizationHeader(),
          },
          body: JSON.stringify({
            certificate: updatedCertificate,
          }),
        }
      )
    }
  )

  const updateCertificatesResults = await Promise.allSettled(
    allCertificateUpdatePromises
  )
  console.log(
    'Update certificates to use new VAT number responses: ',
    updateCertificatesResults
  )

  // delete old company
  const companyDeleteResult = await fetch(
    `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/company-delete`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthorizationHeader(),
      },
      body: JSON.stringify({
        companyVatNumber: oldVatNumber,
      }),
    }
  )

  console.log(
    'Delete company for old VAT number response: ',
    companyDeleteResult
  )
}
